import { create } from 'zustand';

import type { SendFailureInline } from './review-step-logic';

/**
 * Whether the Review step's two big cards — "Review and send" and "Dispatch log" — are
 * expanded. Both start COLLAPSED when a rep arrives on the step, so the page
 * opens to the one-line summary rather than a wall of people and history.
 *
 * Module-scope zustand (CLAUDE.md "Zustand for Module-Scope Client State") and
 * not `useState`: the open/closed choice has to survive the parent re-renders an
 * SSE `artifact:updated` causes, and the Dispatch log is opened from places
 * that are not its descendants — the member panel's "All of Ana's sends" link,
 * a fresh send, a "test already running" refusal — so they write here through
 * {@link openDispatchLog} instead of threading a callback.
 *
 * `PlayFlowShell` resets this with the other play-scoped stores.
 */
interface ReviewCardsState {
  reviewOpen: boolean;
  logOpen: boolean;
  /**
   * The message that stays above the Send button after a test or send did not
   * start (plan 2026-10-15-005), with the Dispatch log's matching note (`null`
   * unless the server refused). Here (not `ReviewStep` state) because a folder's
   * "Test selected" refusal writes it too. `playId` is the play it belongs to — a
   * late failure from play A must never show on play B's footer. Cleared at the
   * next press and on success.
   */
  sendFailure: (SendFailureInline & { playId: string }) | null;
}

/** Exported per the store convention — reset idiom is `useReviewCardsStore.setState(initialState, true)`. */
export const initialState: ReviewCardsState = {
  reviewOpen: false,
  logOpen: false,
  sendFailure: null,
};

export const useReviewCardsStore = create<ReviewCardsState>(() => ({ ...initialState }));

export function setReviewOpen(open: boolean): void {
  useReviewCardsStore.setState({ reviewOpen: open });
}

export function setDispatchLogOpen(open: boolean): void {
  useReviewCardsStore.setState({ logOpen: open });
}

export function setSendFailure(failure: ReviewCardsState['sendFailure']): void {
  useReviewCardsStore.setState({ sendFailure: failure });
}

/** Expands the Review and send card — a refused send calls this so its reason is seen. */
export function openReview(): void {
  useReviewCardsStore.setState({ reviewOpen: true });
}

/** Expands the Dispatch log — whatever sends the rep to look at it calls this first. */
export function openDispatchLog(): void {
  useReviewCardsStore.setState({ logOpen: true });
}

/** The play-scoped reset `PlayFlowShell` performs. */
export function clearReviewCards(): void {
  useReviewCardsStore.setState({ ...initialState });
}
